import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  TextField,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { materialAPI, aiAPI } from '../services/api';
import MaterialLookupDialog from '../components/MaterialLookupDialog';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface MaterialDetailLocationState {
  returnTo?: {
    pathname: string;
    state?: Record<string, unknown>;
  };
}

const MaterialDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as MaterialDetailLocationState | null;
  const [material, setMaterial] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  
  // Lookup Dialog State
  const [lookupDialogOpen, setLookupDialogOpen] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [lookupResults, setLookupResults] = useState<any>(null);

  useEffect(() => {
    if (id && id !== 'new') {
      fetchMaterial();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchMaterial = async () => {
    try {
      const response = await materialAPI.getById(parseInt(id!));
      setMaterial(response.data);
    } catch (error) {
      console.error('Fehler beim Laden des Materials:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLookupClick = async () => {
    if (!material?.id) return;
    
    setLookupDialogOpen(true);
    setLookupLoading(true);
    setLookupError(null);
    setLookupResults(null);
    
    try {
      const response = await aiAPI.lookupMaterial(material.id);
      setLookupResults(response.data);
    } catch (error: any) {
      console.error('Fehler beim Material-Lookup:', error);
      setLookupError(
        error.response?.data?.message || 
        error.response?.data?.error || 
        'Fehler beim Laden der ähnlichen Produkte'
      );
    } finally {
      setLookupLoading(false);
    }
  };

  const handleBack = () => {
    if (locationState?.returnTo?.pathname) {
      navigate(locationState.returnTo.pathname, { state: locationState.returnTo.state });
      return;
    }

    navigate('/materials');
  };

  const hasEndoTodayLink = material?.endo_today_link && material.endo_today_link.trim().length > 0;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
        >
          Zurück
        </Button>
        <Box display="flex" gap={1}>
          {material && hasEndoTodayLink && (
            <Button
              variant="outlined"
              startIcon={<SearchIcon />}
              onClick={handleLookupClick}
              color="primary"
            >
              Ähnliche Produkte suchen
            </Button>
          )}
          {material && (
            <Button
              variant="contained"
              onClick={() => navigate(`/materials/${id}/edit`)}
            >
              Bearbeiten
            </Button>
          )}
        </Box>
      </Box>

      <Typography variant="h4" gutterBottom>
        {material?.name || 'Material Details'}
      </Typography>

      <Paper sx={{ mt: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Grunddaten" />
          <Tab label="Bestand" />
          <Tab label="Barcodes" />
          <Tab label="Historie" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Bezeichnung"
                value={material?.name || ''}
                disabled
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Kategorie"
                value={material?.category_name || ''}
                disabled
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Firma"
                value={material?.company_name || ''}
                disabled
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Schrank"
                value={material?.cabinet_name || ''}
                disabled
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Fach"
                value={material?.compartment_name || 'Kein Fach zugewiesen'}
                disabled
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Größe"
                value={material?.size || ''}
                disabled
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Kosten pro Einheit"
                value={material?.cost ? `${Number(material.cost).toFixed(2)} €` : 'Nicht angegeben'}
                disabled
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Verfallsdatum"
                value={material?.expiry_date ? new Date(material.expiry_date).toLocaleDateString('de-DE') : ''}
                disabled
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Beschreibung"
                value={material?.description || ''}
                disabled
              />
            </Grid>
            
            {/* Device-Eigenschaften */}
            {(material?.shape_name || material?.shaft_length || material?.device_length || 
              material?.device_diameter || material?.french_size || material?.guidewire_acceptance) && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 'bold' }}>
                    Device-Eigenschaften
                  </Typography>
                </Grid>
                {material?.shape_name && (
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Shape / Form"
                      value={material.shape_name}
                      disabled
                    />
                  </Grid>
                )}
                {material?.shaft_length && (
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Schaftlänge"
                      value={material.shaft_length}
                      disabled
                    />
                  </Grid>
                )}
                {material?.device_length && (
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Device-Länge"
                      value={material.device_length}
                      disabled
                    />
                  </Grid>
                )}
                {material?.device_diameter && (
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Device-Durchmesser"
                      value={material.device_diameter}
                      disabled
                    />
                  </Grid>
                )}
                {material?.french_size && (
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="French-Size"
                      value={material.french_size}
                      disabled
                    />
                  </Grid>
                )}
                {material?.guidewire_acceptance && (
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Guidewire-Acceptance"
                      value={material.guidewire_acceptance}
                      disabled
                    />
                  </Grid>
                )}
              </>
            )}
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Aktueller Bestand"
                value={material?.current_stock || 0}
                type="number"
                disabled
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Mindestbestand"
                value={material?.min_stock || 0}
                type="number"
                disabled
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Status"
                value={material?.stock_status || 'OK'}
                disabled
              />
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Typography variant="body1">
            Barcode-Verwaltung wird hier angezeigt
          </Typography>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Typography variant="body1">
            Transaktionshistorie wird hier angezeigt
          </Typography>
        </TabPanel>
      </Paper>

      {/* Material Lookup Dialog */}
      <MaterialLookupDialog
        open={lookupDialogOpen}
        onClose={() => setLookupDialogOpen(false)}
        loading={lookupLoading}
        error={lookupError}
        material={lookupResults?.material}
        sourceUrl={lookupResults?.sourceUrl}
        results={lookupResults?.results}
      />
    </Box>
  );
};

export default MaterialDetail;
